import { NotifyHelper } from 'classes/helpers/notify.helper';
import { MAX_SHOTS_USED } from 'classes/plate-canvas';
import { t } from 'i18next';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { ShotsRoutes } from 'lib_ts/enums/routing/routes/shots';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import { IFindShotsByDateRequest } from 'lib_ts/interfaces/machine-shots/i-find-by-date';
import {
  IAggregateMachineShotsDict,
  IAggregateMachineShotsRequest,
  IMachineShot,
  IMatchingShotsRequest,
} from 'lib_ts/interfaces/training/i-machine-shot';
import { IShotPrecursorSummary } from 'lib_ts/interfaces/training/i-shot-precursor';
import { BaseRESTService } from 'services/_base-rest.service';

const MATCHING_SHOTS_LIMIT = MAX_SHOTS_USED + 1;

/** example service for pulling data from server
 * note the singleton definition so we only ever have 1 instance
 */
export class ShotsService extends BaseRESTService {
  private static instance: ShotsService;
  static getInstance(): ShotsService {
    if (!ShotsService.instance) {
      ShotsService.instance = new ShotsService();
    }

    return ShotsService.instance;
  }

  private constructor() {
    super({
      controller: Controller.Shot,
    });
  }

  async countShotsSince(
    payload: IFindShotsByDateRequest
  ): Promise<number | undefined> {
    return await this.post(
      {
        uri: ShotsRoutes.CountShotsSince,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return undefined;
        }

        return result.data as number;
      })
      .catch((e) => {
        console.error(e);
        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });
        return undefined;
      });
  }

  async countFiresSince(
    payload: IFindShotsByDateRequest
  ): Promise<number | undefined> {
    return await this.post(
      {
        uri: ShotsRoutes.CountFiresSince,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return undefined;
        }

        return result.data as number;
      })
      .catch((e) => {
        console.error(e);
        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });
        return undefined;
      });
  }

  async updateShot(
    shotID: string,
    payload: Partial<IMachineShot>
  ): Promise<IMachineShot | undefined> {
    return await this.put(
      {
        uri: ShotsRoutes.UpdateShot,
        params: {
          shot_id: shotID,
        } as any,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return undefined;
        }

        return result.data as IMachineShot;
      })
      .catch((e) => {
        console.error(e);
        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });
        return undefined;
      });
  }

  async archiveByList(listID: string): Promise<boolean> {
    return await this.get({
      uri: `${ShotsRoutes.ArchiveByList}/${listID}`,
    })
      .then((result) => !!result && result.success)
      .catch((e) => {
        console.error(e);
        return false;
      });
  }

  // key is matching_hash
  async getMatches(
    config: IMatchingShotsRequest,
    limit?: number
  ): Promise<DictOf<IMachineShot[]>> {
    return await this.post(
      {
        uri: ShotsRoutes.GetMatches,
        params: {
          limit: limit ?? MATCHING_SHOTS_LIMIT,
        } as any,
      },
      config
    );
  }

  /** uses matching_hash */
  async getAggMatches(
    payload: IAggregateMachineShotsRequest
  ): Promise<IAggregateMachineShotsDict | undefined> {
    if (!payload.machineID) {
      console.warn('Cannot perform getAggregateMatches without a machineID');
      return undefined;
    }

    return await this.post(
      {
        uri: ShotsRoutes.AggMatches,
      },
      payload
    )
      .then((result) => {
        return result;
      })
      .catch((error) => {
        console.error(error);

        return undefined;
      });
  }

  async getById(shotID: string): Promise<IMachineShot | undefined> {
    return await this.get({
      uri: `${ShotsRoutes.GetById}/${shotID}`,
    })
      .then((result) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return undefined;
        }

        return result.data as IMachineShot;
      })
      .catch((e) => {
        console.error(e);
        return undefined;
      });
  }

  async getByIds(shotIDs: string[]): Promise<IMachineShot[]> {
    return await this.post(
      {
        uri: ShotsRoutes.GetByIds,
      },
      shotIDs
    )
      .then((result) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return [];
        }

        return result.data as IMachineShot[];
      })
      .catch((e) => {
        console.error(e);
        return [];
      });
  }

  async getPrecursorSummary(
    shotID: string
  ): Promise<IShotPrecursorSummary | undefined> {
    return await this.get({
      uri: `${ShotsRoutes.GetPrecursorSummary}/${shotID}`,
    })
      .then((result) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return undefined;
        }

        return result.data as IShotPrecursorSummary;
      })
      .catch((e) => {
        console.error(e);
        return undefined;
      });
  }
}
